import { Copy, Loader2, X } from 'lucide-react';
import {
  extractCursorPrompt,
  healthScoreLabel,
  parseHealthScore,
  stripCursorPromptSection,
} from '../reportUtils';
import type { CachedReport } from '../hooks/useAgentActionReport';
import ReportMarkdown from './ReportMarkdown';

interface ReportPanelProps {
  open: boolean;
  loading: boolean;
  error: string | null;
  result: CachedReport | null;
  agentRuns: number;
  onClose: () => void;
  onRegenerate: () => void;
}

export default function ReportPanel({
  open,
  loading,
  error,
  result,
  agentRuns,
  onClose,
  onRegenerate,
}: ReportPanelProps) {
  if (!open) return null;

  const score = result ? parseHealthScore(result.report) : null;
  const scoreInfo = healthScoreLabel(score);
  const cursorPrompt = result ? extractCursorPrompt(result.report) : '';
  const bodyReport = result ? stripCursorPromptSection(result.report) : '';

  const copyText = async (text: string) => {
    if (!text) return;
    await navigator.clipboard.writeText(text);
  };

  return (
    <div className="mb-4 rounded-lg border border-violet-200 dark:border-violet-800 bg-white dark:bg-gray-900 shadow-md overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200/80 dark:border-gray-700 bg-violet-50/50 dark:bg-violet-950/20">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Platform report</span>
          {result && !loading && (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${scoreInfo.className}`}>
              {scoreInfo.text}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          aria-label="Close report"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {loading && (
        <div className="px-4 py-8 text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin text-violet-600" />
          Claude is analyzing {agentRuns.toLocaleString()} agent actions…
        </div>
      )}

      {error && !loading && (
        <div className="px-4 py-4 text-sm text-red-600 dark:text-red-400">{error}</div>
      )}

      {result && !loading && (
        <div className="px-4 py-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Generated {new Date(result.generated_at).toLocaleString()} · {result.actions_analyzed} actions analyzed
          </p>
          <ReportMarkdown content={bodyReport} />

          {cursorPrompt && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Cursor prompt</h4>
              <pre className="text-xs whitespace-pre-wrap bg-gray-900 text-gray-100 rounded-lg p-3 max-h-64 overflow-auto">
                {cursorPrompt}
              </pre>
            </div>
          )}

          <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-gray-100 dark:border-gray-800">
            <button
              type="button"
              onClick={() => copyText(cursorPrompt)}
              disabled={!cursorPrompt}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40"
            >
              <Copy className="w-3.5 h-3.5" />
              Copy Cursor prompt
            </button>
            <button
              type="button"
              onClick={() => copyText(result.report)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <Copy className="w-3.5 h-3.5" />
              Copy full report
            </button>
            <button
              type="button"
              onClick={onRegenerate}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Regenerate
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
